/**
 * ADR-0052 (slice-3655, Chris 2026-09-15: "it's frustrating when doing copy paste operations
 * when say you have a 5char item id, but you are copying/pasting a massive string with a ton
 * of white space"): the input-side half of PB's behaviour. PB's DataWindow edit controls cap
 * typed AND pasted text at {@code edit.limit} (the column width), and because every PB
 * connection runs {@code TrimSpaces=1} the values people copy out of PB never carry padding.
 *
 * <p>Browsers apply {@code maxLength} to a paste too, but they keep whatever fits — pasting
 * "ABC" plus 27 blanks into a 30-character field fits, blanks and all. This document-level
 * capture listener rewrites a paste into any text-like input or textarea so that:
 * <ul>
 *   <li>trailing whitespace (spaces, tabs, CR/LF — an Excel cell copies with a trailing
 *       newline) is dropped from the pasted text; leading and inner whitespace is kept;</li>
 *   <li>the result is then cut to what {@code maxLength} leaves room for, like PB's
 *       edit.limit;</li>
 *   <li>React sees an ordinary {@code input} event, so controlled fields and react-hook-form
 *       update exactly as if the trimmed text had been typed.</li>
 * </ul>
 * A paste with nothing to trim is left to the browser untouched.
 */

const TEXT_TYPES = new Set(['', 'text', 'search', 'tel', 'url', 'email', 'password']);

/** Trailing whitespace only — the pasted blob's leading/inner characters are the user's. */
export function trimPastedText(text: string): string {
  return text.replace(/\s+$/u, '');
}

function isTextTarget(t: EventTarget | null): t is HTMLInputElement | HTMLTextAreaElement {
  if (t instanceof HTMLTextAreaElement) return !t.readOnly && !t.disabled;
  if (t instanceof HTMLInputElement) {
    return TEXT_TYPES.has(t.type) && !t.readOnly && !t.disabled;
  }
  return false;
}

/** The paste handler itself, exported so tests can call it with a synthetic event. */
export function handlePaste(e: ClipboardEvent): void {
  const el = e.target;
  if (!isTextTarget(el)) return;
  const raw = e.clipboardData?.getData('text/plain');
  if (raw == null || raw === '') return;
  const trimmed = trimPastedText(raw);
  if (trimmed === raw) return; // nothing to do — let the browser paste

  e.preventDefault();
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const room =
    el.maxLength > 0 ? Math.max(0, el.maxLength - (el.value.length - (end - start))) : Infinity;
  const insert = room === Infinity ? trimmed : trimmed.slice(0, room);
  el.setRangeText(insert, start, end, 'end');
  // React (and any native listener) hears this exactly like typing.
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Install once per document (App does it). Returns the uninstaller. */
export function installPasteTrim(doc: Document = document): () => void {
  doc.addEventListener('paste', handlePaste, true);
  return () => doc.removeEventListener('paste', handlePaste, true);
}
